/* @flow */
import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { translate } from 'react-i18next';

import type { TranslatorProps } from 'react-i18next';

import type TypeActionDrawerStore from 'stores/ActionDrawerStore.js';
import AssetStore from 'stores/AssetStore';

import Form from 'forms/_.extend';
import Loading from 'components/Loading/Loading';
import ActionDrawerHeader from 'views/ActionDrawer/components/ActionDrawerHeader/ActionDrawerHeader';
import { isAdmin } from '../../../../../permissions/general.js';

import AllocateAssetToProjectForm from './AllocateAssetToProjectForm/AllocateAssetToProjectForm';
import AllocateAssetToProjectSuccess from './AllocateAssetToProjectSuccess/AllocateAssetToProjectSuccess';
import AllocateAssetToProjectError from './AllocateAssetToProjectError/AllocateAssetToProjectError';

import styles from '../../../ActionDrawer.module.scss';

type TypeProps = {|
  actionDrawerStore: TypeActionDrawerStore,
  assetStore: AssetStore,
|} & TranslatorProps;

@translate()
@inject('actionDrawerStore', 'assetStore')
@observer
export default class AllocateAssetToProject extends React.Component<*> {
  createForm = () => {
    const { t, actionDrawerStore, assetStore }: TypeProps = this.props;
    const assets = this.props.formData;
    const fields = [
      {
        name: 'allocateProject',
        label: t('general:allocate_asset_to_project'),
        fields: [
          {
            name: 'project',
            label: t('general:project'),
            rules: 'required|string',
            value: null,
            placeholder: t('general:placeholders.project_code'),
          },
        ],
      },
      {
        name: 'allocateProjectWarnings',
        fields: assets.map(asset => {
          return {
            name: `${asset.id}`,
            label: t('general:project'),
            rules: 'project_overlap',
            value: {
              calibration_expire: false,
              rental_expire: false,
              asset_in_use: false,
              asset_scheduled_use: false,
            },
            related: ['allocateProject.project'],
            options: {
              validateOnChange: true,
            },
            extra: {
              warnings: [
                {
                  name: 'calibration_expire',
                  message: t('general:assets.messages.calibration_expires_during_project'),
                  link: null,
                },
                {
                  name: 'rental_expire',
                  message: t('general:assets.rented_assets.messages.rental_expires_during_project'),
                  link: null,
                },
                {
                  name: 'asset_in_use',
                  message: t('general:assets.messages.in_use_on_project'),
                  link: asset.project ? `/projects/${asset.project.id}` : null,
                },
                {
                  name: 'asset_scheduled_use',
                  message: t('general:assets.messages.scheduled_for_another_project'),
                  link: asset.project ? `/projects/${asset.project.id}` : null,
                },
              ],
            },
          };
        }),
      },
    ];

    const hooks = {
      onInit() {
        if (assets.length > 0) {
          assetStore.loadAssets(assets.map(item => item.id));
        }
      },
      onSuccess(data: Object) {
        const { project }: Object = data.values().allocateProject;

        assets.forEach(asset => {
          actionDrawerStore.allocateAssetToProject(asset.id, project, !isAdmin());
        });
      },
    };

    return new Form(
      { fields },
      {
        hooks,
        name: 'allocateAssetToProject',
      }
    );
  };

  render(): React.Element<*> {
    const { t, actionDrawerStore }: TypeProps = this.props;
    const assets = this.props.formData;
    const title: string = isAdmin()
      ? t('general:actions.allocate_to_project')
      : t('general:actions.allocate_to_project_request');

    if (actionDrawerStore.loading) {
      return <Loading />;
    }

    if (actionDrawerStore.error) {
      return (
        <div className={`${styles['action-drawer-form']} ${styles.error}`}>
          <ActionDrawerHeader title={title} />
          <AllocateAssetToProjectError assets={assets} />
        </div>
      );
    }

    if (actionDrawerStore.success) {
      return (
        <div className={`${styles['action-drawer-form']} ${styles.success}`}>
          <ActionDrawerHeader title={title} />
          <AllocateAssetToProjectSuccess response={actionDrawerStore.response} />
        </div>
      );
    }

    return (
      <div className={styles['action-drawer-form']}>
        <ActionDrawerHeader title={title} />
        <AllocateAssetToProjectForm assets={assets} form={this.createForm()} />
      </div>
    );
  }
}
